import { useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError, decideActionRequestMutation, listActionRequestsOptions, listActionRequestsQueryKey } from "@/api";

export type { ActionRequestResponse as ActionRequest } from "@/api";

const ERROR_CODES = [
  "ACTION_REQUEST_NOT_FOUND",
  "ACTION_REQUEST_NOT_PENDING",
  "ACTION_REQUEST_EXPIRED",
  "ACTION_REQUEST_INVALID",
  "ACTION_REQUEST_FORBIDDEN",
] as const;
type ErrorCode = (typeof ERROR_CODES)[number];
const isErrorCode = (code: unknown): code is ErrorCode =>
  typeof code === "string" && (ERROR_CODES as readonly string[]).includes(code);

/** Write actions flows paused for, waiting for me to approve (MHS-972). */
export function useApprovals() {
  return useQuery({ ...listActionRequestsOptions(), select: (r) => r.data });
}

/** Approve or decline one: the run goes on in the background, and the inbox refreshes. */
export function useDecideApproval() {
  const { t } = useTranslation("workflows", { keyPrefix: "approvals" });
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    ...decideActionRequestMutation(),
    onSuccess: (_res, variables) => {
      let done: "alwaysAllowed" | "approved" | "declined" = variables.body.decision === "run" ? "approved" : "declined";
      if (variables.body.remember) done = "alwaysAllowed";
      toast.success(t(done));
      return queryClient.invalidateQueries({ queryKey: listActionRequestsQueryKey() });
    },
    onError: (error) => {
      const code = error instanceof ApiError ? error.code : undefined;
      toast.error(isErrorCode(code) ? t(`errors.${code}`) : t("errors.failed"));
      return queryClient.invalidateQueries({ queryKey: listActionRequestsQueryKey() });
    },
  });
}
